/**
 * The microphone and the speaker, settled before a call needs them.
 *
 * A browser hands out the microphone once the person says so, and it asks the first time
 * something reaches for it. Until this module the first reach was `getUserMedia` inside
 * `verto.answer` — so a rep's very first call of their first shift rang, they pressed Answer,
 * and Chrome put a permission bar over the top of the page while the lead listened to silence.
 * A refusal, or a rep who answered the bar instead of the caller, lost the call outright.
 *
 * So the permission is asked for at sign-in, from a dialog the rep can read, and the devices
 * they pick are remembered. Three things come out of that and are used elsewhere:
 *
 *   - the **microphone** a call captures from (`micConstraints`, applied in
 *     `softphone-context.tsx` on every dial and every answer),
 *   - the **speaker** the far end and the ringer play through (`RemoteAudio.setSink`,
 *     `Tones.setSink`),
 *   - whether the browser will let us have a microphone at all, which is what the dock's
 *     warning and the dialog's own copy are about.
 *
 * **Per browser, not per account** — as the session lock, auto-answer and the ringer next door
 * are, and for the same reason: a device id describes the seat. The rep at a desk with a USB
 * headset and the same person on a laptop at home are two seats, and a preference that followed
 * the account would send one of them into the wrong microphone. `MediaDeviceInfo.deviceId` is
 * scoped to the origin anyway; it means nothing anywhere else.
 *
 * **Nothing here opens a stream except `probe`.** Enumerating devices, reading a permission and
 * deciding what to ask for are all cheap and side-effect free; taking the microphone is the one
 * act a rep notices (the browser lights an indicator), so it happens in the dialog, on a click,
 * and the stream is stopped when the dialog closes.
 */

/** A device as the picker shows it: a stable id and something a person can read. */
export interface AudioDevice {
  id: string;
  label: string;
}

/**
 * What the browser will do the next time we ask for the microphone.
 *
 * `unavailable` is not a refusal — it is a browser that has no `mediaDevices` to ask, which in
 * practice means the console was opened over plain http on something other than localhost. The
 * distinction matters because the fix is different: a denial is fixed in the site's permissions,
 * an unavailable one only by reaching the console over https.
 */
export type MicPermission = "granted" | "denied" | "prompt" | "unavailable";

export interface AudioPrefs {
  /** The chosen microphone, or null for whatever the system calls default. */
  micId: string | null;
  /** The chosen speaker, or null for the system default. */
  speakerId: string | null;
}

export const NO_AUDIO_PREFS: AudioPrefs = { micId: null, speakerId: null };

const STORAGE_KEY = "acl.softphone.devices";

/* ------------------------------------------------------------------------------------------ */
/* What the rep chose                                                                          */
/* ------------------------------------------------------------------------------------------ */

/**
 * Read the saved choice.
 *
 * Tolerant on purpose: a half-written or hand-edited record must not stop a phone registering,
 * so anything unreadable reads as "nothing chosen", which is the same as a new browser and puts
 * every call on the system default.
 */
export function readAudioPrefs(): AudioPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return NO_AUDIO_PREFS;
    const saved = JSON.parse(raw) as Partial<AudioPrefs>;
    return {
      micId: typeof saved.micId === "string" && saved.micId ? saved.micId : null,
      speakerId: typeof saved.speakerId === "string" && saved.speakerId ? saved.speakerId : null,
    };
  } catch {
    return NO_AUDIO_PREFS;
  }
}

type PrefsListener = (prefs: AudioPrefs) => void;
const listeners = new Set<PrefsListener>();

/**
 * Save the choice and tell whoever is holding audio right now.
 *
 * The speaker has to reach a live call: a rep who opens the dialog mid-shift because the far end
 * is coming out of the laptop rather than their headset expects the change to land on the call
 * they are on, not on the next one. `softphone-context.tsx` subscribes and re-points the audio
 * element and the ringer. The microphone cannot move mid-call without renegotiating the media,
 * so it applies from the next call — the dialog says so rather than pretending otherwise.
 */
export function writeAudioPrefs(prefs: AudioPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* non-fatal — the choice simply will not survive a reload */
  }
  for (const listener of [...listeners]) listener(prefs);
}

export function subscribeAudioPrefs(listener: PrefsListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ------------------------------------------------------------------------------------------ */
/* What the browser will give us                                                               */
/* ------------------------------------------------------------------------------------------ */

/** Whether this page can ask for a microphone at all. False on an insecure origin. */
export function mediaSupported(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";
}

/**
 * Whether the browser can send audio to a chosen speaker.
 *
 * `setSinkId` is Chromium's and recent Firefox's; Safari has never had it and plays everything
 * through the system default. Asked and answered in one place because three surfaces need it:
 * the dialog hides the speaker picker without it, the settings page says why, and
 * `RemoteAudio`/`Tones` skip the call.
 */
export function speakerChoiceSupported(): boolean {
  return (
    typeof HTMLMediaElement !== "undefined" &&
    typeof HTMLMediaElement.prototype.setSinkId === "function"
  );
}

/** The shape of `MediaDeviceInfo` this module actually reads — so tests need no browser. */
export interface DeviceInfoLike {
  deviceId: string;
  kind: string;
  label: string;
}

/**
 * Turn `enumerateDevices()` into a list a person can choose from.
 *
 * Two browser facts are dealt with here rather than at three call sites:
 *
 * 1. **Labels are empty until the microphone has been granted.** A browser that named every
 *    device before permission would be a fingerprinting surface, so an unasked page sees a list
 *    of blanks. A blank is still worth showing once we are granted and one device simply has no
 *    name, hence the positional fallback.
 * 2. **Chrome invents two devices**: `default` (whatever the system is set to) and
 *    `communications` (Windows' "communications" role). Both are the same hardware listed again
 *    under another name, and the picker already offers "system default" as its first option —
 *    which is the same thing, expressed as asking for no particular device — so both are
 *    dropped. A list that offered one headset three times would read as a bug.
 */
export function describeDevices(
  devices: readonly DeviceInfoLike[],
  kind: "audioinput" | "audiooutput",
): AudioDevice[] {
  const noun = kind === "audioinput" ? "Microphone" : "Speaker";
  return devices
    .filter((d) => d.kind === kind && d.deviceId !== "communications" && d.deviceId !== "default")
    .map((d, index) => ({
      id: d.deviceId,
      label: d.label?.trim() ? d.label.trim() : `${noun} ${index + 1}`,
    }));
}

/**
 * Read the permission without asking for it.
 *
 * `navigator.permissions.query({name: "microphone"})` is the honest answer and Chromium gives
 * it. Firefox and Safari reject or throw on that descriptor, so the fallback reads the device
 * list: a browser that hands out labels has already granted us the microphone at some point.
 * The fallback can say "prompt" where a grant exists but is not remembered (Firefox's default),
 * which costs the rep one extra click on a button that was going to be there anyway — the wrong
 * way round to be wrong, which is why it is the fallback and not the other one.
 */
export async function micPermission(): Promise<MicPermission> {
  if (!mediaSupported()) return "unavailable";
  try {
    // The descriptor name is not in every lib.dom; the cast keeps the call honest about that.
    const status = await navigator.permissions?.query({ name: "microphone" as PermissionName });
    if (status?.state === "granted" || status?.state === "denied" || status?.state === "prompt") {
      return status.state;
    }
  } catch {
    /* not supported here — fall through to the device list */
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return permissionFromDevices(devices);
  } catch {
    return "prompt";
  }
}

/** The fallback reading, split out so it can be tested without a browser. */
export function permissionFromDevices(devices: readonly DeviceInfoLike[]): MicPermission {
  const mics = devices.filter((d) => d.kind === "audioinput");
  return mics.some((d) => d.label?.trim()) ? "granted" : "prompt";
}

/** The microphones and speakers this browser can offer, already named and de-duplicated. */
export async function listAudioDevices(): Promise<{ mics: AudioDevice[]; speakers: AudioDevice[] }> {
  if (!mediaSupported()) return { mics: [], speakers: [] };
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      mics: describeDevices(devices, "audioinput"),
      speakers: describeDevices(devices, "audiooutput"),
    };
  } catch {
    return { mics: [], speakers: [] };
  }
}

/**
 * Open the microphone — the one call that makes the browser ask.
 *
 * Returns the stream so the dialog can show the rep their own level; every caller is responsible
 * for stopping its tracks. Failures are the browser's own (`NotAllowedError` for a refusal,
 * `NotFoundError` for a machine with no microphone), and are handed on unchanged so the dialog
 * can say which happened.
 */
export async function probe(micId: string | null): Promise<MediaStream> {
  if (!mediaSupported()) throw new DOMException("No microphone here", "NotFoundError");
  return navigator.mediaDevices.getUserMedia({ audio: constraintsFor(micId), video: false });
}

/* ------------------------------------------------------------------------------------------ */
/* What a call asks for                                                                        */
/* ------------------------------------------------------------------------------------------ */

function constraintsFor(micId: string | null): MediaTrackConstraints {
  // `channelCount: 1` on every capture, chosen device or not. A stereo microphone (an interface,
  // an aggregate device, a loopback) makes Chrome negotiate `stereo=1`, FreeSWITCH then runs the
  // leg on mod_opus's two-channel implementation, and a media bug on that leg reads its
  // interleaved L/R as consecutive mono samples — which is scrambled audio for the transcription
  // tap and for anything else listening in. Not `{exact: 1}`: a device that cannot do one channel
  // would fail `getUserMedia` outright and the call with it, where the ideal form downmixes.
  return micId ? { deviceId: { exact: micId }, channelCount: 1 } : { channelCount: 1 };
}

/**
 * The audio constraint for a call, given what the rep picked and what is plugged in now.
 *
 * `deviceId: {exact}` is used rather than `{ideal}` deliberately, and the presence check is what
 * makes that safe. A rep picks their USB headset in the morning and takes it home; `exact` on a
 * device that is gone rejects with `OverconstrainedError` and the call fails to connect, while
 * `ideal` silently falls back — to the laptop microphone, in the middle of a conversation, with
 * nothing on screen to say so. Neither is acceptable, so the saved id is checked against the
 * devices that exist and dropped when it is not among them: the call goes ahead on the default,
 * and the caller can tell the rep their headset is not there.
 *
 * An empty device list means the enumeration failed, not that the machine has no hardware —
 * in that case the saved choice is honoured, since second-guessing it would move every call to
 * the default on a transient failure.
 */
export function micConstraints(
  micId: string | null,
  mics: readonly AudioDevice[],
): { audio: MediaTrackConstraints; fallback: boolean } {
  if (!micId) return { audio: constraintsFor(null), fallback: false };
  if (mics.length === 0) return { audio: constraintsFor(micId), fallback: false };
  const present = mics.some((m) => m.id === micId);
  return { audio: constraintsFor(present ? micId : null), fallback: !present };
}

/**
 * The same decision, against the devices the browser has right now.
 *
 * Called on the way into every dial and every answer. An enumeration costs a millisecond or two
 * and cannot be cached: the point of it is to notice hardware that was unplugged since the last
 * call.
 */
export async function micForCall(
  prefs: AudioPrefs = readAudioPrefs(),
): Promise<{ audio: MediaTrackConstraints; deviceId: string | undefined; fallback: boolean }> {
  if (!prefs.micId) return { audio: constraintsFor(null), deviceId: undefined, fallback: false };
  const { mics } = await listAudioDevices();
  const decided = micConstraints(prefs.micId, mics);
  return {
    audio: decided.audio,
    // The vendored client takes a bare id on `call()`; `answer()` takes the constraint. Both
    // readings of the same decision, so they are computed together and cannot disagree.
    deviceId: decided.fallback ? undefined : prefs.micId,
    fallback: decided.fallback,
  };
}

/* ------------------------------------------------------------------------------------------ */
/* When to ask                                                                                 */
/* ------------------------------------------------------------------------------------------ */

/**
 * Whether to open the setup dialog by itself, for a rep who has just signed in.
 *
 * Every sign-in, whatever this browser settled on before. The saved choice is not the question:
 * a rep signs in at a different desk, on a shared machine, or with yesterday's headset unplugged,
 * and the first they hear of it is a lead who cannot hear them. Showing the meter and the two
 * pickers at the one moment in a shift when nobody is waiting on the line costs a keypress and
 * settles it, so the dialog asks again rather than trusting a `completedAt` from last month.
 *
 * The saved devices are still what the dialog opens on, so a rep whose headset is where they left
 * it confirms it and carries on.
 *
 * `unavailable` is the one case it stays shut for: an insecure origin has no microphone to offer
 * and nothing the rep does in the dialog could change that, so the phone says so instead.
 */
export function shouldOfferSetup(permission: MicPermission): boolean {
  return permission !== "unavailable";
}

/** What to show for a saved device: its name, or why it is not being used. */
export function deviceLabel(devices: readonly AudioDevice[], id: string | null): string {
  if (!id) return "System default";
  const found = devices.find((d) => d.id === id);
  return found ? found.label : "Not connected — using the system default";
}

/* ------------------------------------------------------------------------------------------ */
/* The level meter                                                                             */
/* ------------------------------------------------------------------------------------------ */

/** Quieter than this is the room, not the rep. Around a whisper at arm's length. */
const FLOOR_DB = -60;
/** Loud enough that the bar has nothing left to say. Speech peaks reach here when shouting. */
const CEILING_DB = -6;
/** Above this fraction of the bar, somebody is talking rather than breathing near the desk. */
export const SPEAKING = 0.25;

/**
 * A microphone's loudness as a fraction of the bar, on the scale ears use.
 *
 * **Not the raw RMS.** Sound is perceived logarithmically and a microphone's RMS is a tiny number:
 * ordinary speech at a headset boom sits around 0.02 to 0.1 — under a tenth of the bar if that
 * number is used as a width, which is what the first version of this meter did. It read as a
 * broken meter, and Mustafa had to shout to move it (2026-09-17). Decibels put the same speech
 * between half and three quarters of the bar, where a person can see the difference between
 * "you are on" and "you are loud".
 *
 * -60 dBFS to -6 dBFS, clamped at both ends: below the floor is room noise, and the top of the
 * bar is a shout. `SPEAKING` is where the meter starts calling it a voice.
 */
export function meterLevel(rms: number): number {
  if (!(rms > 0)) return 0;
  const db = 20 * Math.log10(rms);
  const fraction = (db - FLOOR_DB) / (CEILING_DB - FLOOR_DB);
  return Math.max(0, Math.min(1, fraction));
}

/**
 * How the bar moves between two readings: straight up, gently down.
 *
 * A meter that follows its input exactly flickers, because speech is mostly gaps — every
 * consonant reads as silence and the bar strobes. Instant attack keeps it honest about the loud
 * moment; a slow release is what makes a sentence look like one movement instead of twenty.
 *
 * `release` is how much of the bar to give up on THIS step, so the caller can work it out from
 * the time since the last one (`FALL_MS`). Per frame would have been simpler and wrong: a browser
 * throttles `requestAnimationFrame` in a background or hidden tab, and a meter tied to frames
 * then takes three seconds to empty instead of a quarter of one.
 */
export function decayLevel(previous: number, next: number, release: number): number {
  return next >= previous ? next : Math.max(next, previous - release);
}

/** How long a full bar takes to fall to nothing, in milliseconds. */
export const FALL_MS = 250;

/* ------------------------------------------------------------------------------------------ */
/* The test sound                                                                              */
/* ------------------------------------------------------------------------------------------ */

const TEST_TONE_RATE = 24_000;

/**
 * A short chime, as a `data:` URI, for the speaker test.
 *
 * A file rather than the Web Audio the ringer uses, because this one has to come out of a
 * *chosen* speaker: `setSinkId` lives on the media element, and an `<audio>` with a src is the
 * one path to it that every browser with the feature agrees on. `AudioContext.setSinkId` exists
 * in recent Chromium only.
 *
 * Generated rather than shipped for the reason `tones.ts` gives: an asset is a fetch that can
 * fail at the moment it is needed, and this is a few lines of arithmetic. Two struck notes an
 * octave apart, decaying — near enough the ring the rep will hear later that the test means
 * something.
 */
export function testToneWav(): string {
  const seconds = 0.7;
  const frames = Math.floor(TEST_TONE_RATE * seconds);
  const bytes = new Uint8Array(44 + frames * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };
  // Canonical 44-byte PCM header: mono, 16-bit, one chunk.
  ascii(0, "RIFF");
  view.setUint32(4, 36 + frames * 2, true);
  ascii(8, "WAVEfmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TEST_TONE_RATE, true);
  view.setUint32(28, TEST_TONE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, "data");
  view.setUint32(40, frames * 2, true);

  for (let i = 0; i < frames; i += 1) {
    const t = i / TEST_TONE_RATE;
    // The second note is struck a fifth of a second after the first, both fading over ~0.45 s.
    const first = Math.sin(2 * Math.PI * 783.99 * t) * Math.exp(-t / 0.22);
    const late = Math.max(0, t - 0.2);
    const second = t < 0.2 ? 0 : Math.sin(2 * Math.PI * 1174.66 * late) * Math.exp(-late / 0.22);
    const sample = (first + second) * 0.28;
    view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, sample)) * 0x7fff, true);
  }

  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${btoa(binary)}`;
}
